// Die Mitspieler im Client (task-016, docs/architektur-paket-2.md, Abschnitt „Client“, ADR 0032 und 0033):
// wer gerade da ist und wo er zu sehen ist. Reine Logik ohne DOM und Three.js; alle Zeiten sind
// Eingangszeiten in Millisekunden, so wie main.ts sie mit `performance.now()` liefert.
//
// Wer da ist, sagen nur `hello.players`, `playerJoined` und `playerLeft`. Eine `players`-Nachricht
// bewegt bekannte Mitspieler; Unbekannte übergeht sie, und den eigenen Eintrag auch – den schickt der
// Server nur, um eine unmögliche Meldung zurückzunehmen (game/bewegung.ts).
//
// Die Bewegung: Jede neue Pose wird ein Stützpunkt, der `MITSPIELER_VERZUG_MS` nach ihrem Eingang
// erreicht ist; dazwischen wird linear gemischt. Vier Regeln halten das ohne Stocken und – außer nach
// langem Stau – ohne Sprung:
//   1. **Nachführen.** Steht der Mitspieler schon auf dem letzten Stützpunkt, beginnt der neue Weg dort
//      und jetzt, nicht an einem Zeitpunkt in der Vergangenheit. Darum springt nie etwas, wenn eine
//      Nachricht ankommt.
//   2. **Soll-Abstand.** Der Server schickt im Takt von `POSEN_ABSTAND_MS`. Ein neuer Stützpunkt liegt
//      darum so weit hinter dem vorigen, solange das höchstens `SPIELRAUM_MS` von „Eingang plus Verzug“
//      abweicht. Schwankende Eingänge ändern so nur ein wenig das Tempo.
//   3. **Mindestabstand.** Kommen nach einem Stau mehrere Posen zugleich an, liegen ihre Stützpunkte
//      trotzdem mindestens `MINDESTABSTAND_MS` auseinander: Der Mitspieler holt mit höchstens doppeltem
//      Tempo auf, statt einen Schritt in einem Bild zu gehen.
//   4. **Rückstand.** Läge ein neuer Stützpunkt mehr als `MAX_RUECKSTAND_MS` hinter „Eingang plus Verzug“,
//      steht der Mitspieler sofort dort – wie jemand, der weiter als `SPRUNG_AB` entfernt auftaucht.
// Gleiche Posen hintereinander (er steht) ergeben keinen Stützpunkt.
import { lerp, type AvatarColors, type PlayerInfo, type Vec3 } from '@bauernhof/shared';

/** So lange nach ihrem Eingang ist eine Pose erreicht. */
export const MITSPIELER_VERZUG_MS = 100;
/** Takt, in dem der Server die Posen schickt (zehnmal je Sekunde). */
export const POSEN_ABSTAND_MS = 100;
/** So weit darf ein Stützpunkt vom Soll-Abstand abweichen, um „Eingang plus Verzug“ zu treffen. */
export const SPIELRAUM_MS = 40;
/**
 * Enger liegen zwei Stützpunkte nie beieinander, auch nicht beim Aufholen nach einem Stau: Ein Mitspieler geht
 * so höchstens doppelt so schnell wie gemeldet, statt einen ganzen Schritt in einem Bild zu springen.
 */
export const MINDESTABSTAND_MS = POSEN_ABSTAND_MS / 2;
/**
 * Weiter als so viele Millisekunden hinter „Eingang plus Verzug“ fällt niemand zurück. Wer nach einem langen Stau
 * so weit hinten wäre, steht sofort an der neuen Stelle – wie bei `SPRUNG_AB` –, statt sekundenlang doppelt so
 * schnell hinterherzulaufen.
 */
export const MAX_RUECKSTAND_MS = 500;
/** Weiter als so viele Blöcke gleitet niemand: Dann steht er sofort an der neuen Stelle. */
export const SPRUNG_AB = 8;

/** Kleinere Abweichungen gelten nicht als neue Pose (Blöcke bzw. Bogenmaß). */
const EPSILON = 1e-6;

/** Wer ein Mitspieler ist: Kennung, Name und Farben. */
export interface Mitspieler {
  id: number;
  name: string;
  avatar: AvatarColors;
}

/** Ort und Haltung, wie sie gezeichnet werden. */
export interface Pose {
  pos: Vec3;
  yaw: number;
  flying: boolean;
}

/** Ein Mitspieler zu einem Zeitpunkt. */
export interface MitspielerStand extends Mitspieler, Pose {}

/** Eine Pose, die zur Zeit `zeit` erreicht ist. */
export interface Stuetzpunkt extends Pose {
  zeit: number;
}

export interface RemotePlayers {
  /** `hello`: Die Liste ersetzt alle bisherigen Mitspieler. */
  begruessung(liste: readonly PlayerInfo[], jetzt: number): void;
  /** `playerJoined`: nimmt auf, oder zieht bei einem bekannten Mitspieler Name und Farben nach. */
  kommt(info: PlayerInfo, jetzt: number): void;
  /** `playerLeft`: Er ist weg. */
  geht(id: number): void;
  /** `players`: neue Posen für bekannte Mitspieler. */
  posen(liste: readonly (Pose & { id: number })[], jetzt: number): void;
  /** Alle Mitspieler zur Zeit `jetzt`, in der Reihenfolge ihres Auftauchens. */
  staende(jetzt: number): MitspielerStand[];
  /** Wer gerade da ist, ohne Ort. */
  liste(): Mitspieler[];
}

interface Eintrag {
  mitspieler: Mitspieler;
  punkte: Stuetzpunkt[];
}

export function createRemotePlayers(eigeneId: number): RemotePlayers {
  const eintraege = new Map<number, Eintrag>();

  const kommt = (info: PlayerInfo, jetzt: number): void => {
    if (info.id === eigeneId) return;
    const mitspieler: Mitspieler = { id: info.id, name: info.name, avatar: { ...info.avatar } };
    const vorhanden = eintraege.get(info.id);
    if (vorhanden === undefined) {
      eintraege.set(info.id, { mitspieler, punkte: [stuetzpunkt(info, jetzt)] });
      return;
    }
    vorhanden.mitspieler = mitspieler;
    fuegeHinzu(vorhanden.punkte, info, jetzt);
  };

  return {
    begruessung(liste, jetzt) {
      eintraege.clear();
      for (const info of liste) kommt(info, jetzt);
    },

    kommt,

    geht(id) {
      eintraege.delete(id);
    },

    posen(liste, jetzt) {
      for (const pose of liste) {
        const eintrag = eintraege.get(pose.id);
        if (eintrag !== undefined) fuegeHinzu(eintrag.punkte, pose, jetzt);
      }
    },

    staende(jetzt) {
      return [...eintraege.values()].map(({ mitspieler, punkte }) => ({
        ...kopieMitspieler(mitspieler),
        ...poseZurZeit(punkte, jetzt),
      }));
    },

    liste() {
      return [...eintraege.values()].map(({ mitspieler }) => kopieMitspieler(mitspieler));
    },
  };
}

/** Hängt eine neue Pose als Stützpunkt an – nach den Regeln aus dem Kopf dieser Datei. */
function fuegeHinzu(punkte: Stuetzpunkt[], pose: Pose, jetzt: number): void {
  // Was vorbei ist, wird nicht mehr gebraucht; der letzte erreichte Stützpunkt bleibt.
  for (let zweiter = punkte[1]; zweiter !== undefined && zweiter.zeit <= jetzt; zweiter = punkte[1]) punkte.shift();
  const letzter = punkte.at(-1);
  if (letzter === undefined || weit(letzter.pos, pose.pos)) {
    punkte.splice(0, punkte.length, stuetzpunkt(pose, jetzt));
    return;
  }
  if (gleich(letzter, pose)) return;
  // Nachführen: Er steht schon auf dem letzten Stützpunkt, also geht es von hier und jetzt weiter.
  if (letzter.zeit < jetzt) letzter.zeit = jetzt;
  const ziel = jetzt + MITSPIELER_VERZUG_MS;
  const soll = Math.min(Math.max(letzter.zeit + POSEN_ABSTAND_MS, ziel - SPIELRAUM_MS), ziel + SPIELRAUM_MS);
  const zeit = Math.max(soll, letzter.zeit + MINDESTABSTAND_MS);
  // Zu weit im Rückstand: Er steht sofort an der neuen Stelle, statt sekundenlang hinterherzulaufen.
  if (zeit - ziel > MAX_RUECKSTAND_MS) {
    punkte.splice(0, punkte.length, stuetzpunkt(pose, jetzt));
    return;
  }
  punkte.push(stuetzpunkt(pose, zeit));
}

/** Die Pose zur Zeit `zeit`: zwischen den beiden Stützpunkten davor und danach, am Rand der Rand. */
export function poseZurZeit(punkte: readonly Stuetzpunkt[], zeit: number): Pose {
  let vorher = punkte[0];
  if (vorher === undefined) throw new Error('Eine Pose braucht mindestens einen Stützpunkt.');
  for (const nachher of punkte) {
    if (zeit < nachher.zeit) {
      if (zeit <= vorher.zeit) return kopiePose(vorher);
      const t = (zeit - vorher.zeit) / (nachher.zeit - vorher.zeit);
      return {
        pos: {
          x: lerp(vorher.pos.x, nachher.pos.x, t),
          y: lerp(vorher.pos.y, nachher.pos.y, t),
          z: lerp(vorher.pos.z, nachher.pos.z, t),
        },
        yaw: kuerzesterWinkel(vorher.yaw, nachher.yaw, t),
        flying: nachher.flying,
      };
    }
    vorher = nachher;
  }
  return kopiePose(vorher);
}

function stuetzpunkt(pose: Pose, zeit: number): Stuetzpunkt {
  return { zeit, ...kopiePose(pose) };
}

function kopiePose(pose: Pose): Pose {
  return { pos: { x: pose.pos.x, y: pose.pos.y, z: pose.pos.z }, yaw: pose.yaw, flying: pose.flying };
}

function kopieMitspieler(mitspieler: Mitspieler): Mitspieler {
  return { id: mitspieler.id, name: mitspieler.name, avatar: { ...mitspieler.avatar } };
}

function gleich(a: Pose, b: Pose): boolean {
  return (
    Math.abs(a.pos.x - b.pos.x) <= EPSILON &&
    Math.abs(a.pos.y - b.pos.y) <= EPSILON &&
    Math.abs(a.pos.z - b.pos.z) <= EPSILON &&
    Math.abs(a.yaw - b.yaw) <= EPSILON &&
    a.flying === b.flying
  );
}

function weit(a: Vec3, b: Vec3): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) > SPRUNG_AB;
}

/** Mischt zwei Winkel über den kürzesten Weg. */
function kuerzesterWinkel(a: number, b: number, t: number): number {
  const zweiPi = 2 * Math.PI;
  const diff = ((((b - a + Math.PI) % zweiPi) + zweiPi) % zweiPi) - Math.PI;
  return a + diff * t;
}
